import { getIsGreetingScene, } from "@memento/types/compilation-theme";
import { getCircleGreetingSceneIndividualMessageDurationSeconds, getGreetingSceneIndividualMessageDurationSeconds, getIndividualClipDurationInFrames, getSceneDurationInSeconds, } from "./getSceneDurationInSeconds.js";
import { getTiming, getTransitionDuration } from "./transitions-lib.js";
import { getStartFrameOfNextItem, toFrames } from "./frames-lib.js";
import { assertNever } from "../type-witchcraft.js";
/**
 * This function should be used by getSubSceneSkipToFrames and by the individual
 * caption components to get the duration of the caption transition.
 */
export function getCaptionTransitionDurationInFrames({ caption, fps, }) {
    const variant = caption.variant;
    if (variant === "default") {
        return {
            variant,
            enter: {
                transitionDurationInFrames: toFrames(0.5, fps),
                descriptionStaggerInFrames: toFrames(0.333, fps),
            },
        };
    }
    else if (variant === "circle") {
        return { variant, enter: toFrames(1, fps) };
    }
    else if (variant === "image-stretch") {
        return { variant, enter: toFrames(1, fps) };
    }
    else if (variant === "slide-in-static") {
        const transitionDurationSeconds = caption.transitionDurationSeconds || 0.5;
        return {
            variant,
            enter: toFrames(transitionDurationSeconds, fps),
        };
    }
    else if (variant === "candles") {
        return { variant, enter: toFrames(0.5, fps) };
    }
    else {
        assertNever(variant);
    }
}
/**
 * Get the maximum amount of time it takes for the caption to enter, in frames.
 */
function getMaxCaptionTransitionEnterDurationInFrames(props) {
    const transitionDurationInfo = getCaptionTransitionDurationInFrames(props);
    const variant = transitionDurationInfo.variant;
    if (variant === "default") {
        return (transitionDurationInfo.enter.transitionDurationInFrames +
            transitionDurationInfo.enter.descriptionStaggerInFrames);
    }
    else {
        return transitionDurationInfo.enter;
    }
}
function getNextSceneStartFrame({ renderConfig, theme, fps, currentSceneStartFrame, currentScene, }) {
    const currentSceneDurationInFrames = toFrames(getSceneDurationInSeconds(currentScene, renderConfig, theme), fps);
    if (currentSceneDurationInFrames === 0) {
        // scene duration of 0 indicates this scene is not enabled
        return currentSceneStartFrame;
    }
    const { transitionDurationInFrames } = getTransitionDuration({
        exitTransition: currentScene.exitTransition,
        sceneTransition: theme.styles.sceneTransition,
        fps,
    });
    // we subtract off the transition duration because the next scene will
    // overlap the current scene by the transition duration, so the next scene
    // will start currentSceneTransitionDurationInFrames before the current
    // scene ends
    const currentSceneDurationWithoutTransitionInFrames = currentSceneDurationInFrames - transitionDurationInFrames;
    if (currentSceneDurationWithoutTransitionInFrames <= 0) {
        return currentSceneStartFrame;
    }
    const nextSceneStartFrame = getStartFrameOfNextItem({
        currentItemStartFrame: currentSceneStartFrame,
        currentItemDuration: currentSceneDurationWithoutTransitionInFrames,
    });
    return nextSceneStartFrame;
}
function getGreetingMessageDurationInFrames({ scene, greetingText, fps, }) {
    const sceneType = scene.type;
    if (sceneType === "typeOnGreeting" ||
        sceneType === "greeting" ||
        sceneType === "greetingLinesAsScenes") {
        return toFrames(getGreetingSceneIndividualMessageDurationSeconds(scene, greetingText), fps);
    }
    else if (sceneType === "circleGreeting") {
        return toFrames(getCircleGreetingSceneIndividualMessageDurationSeconds(scene), fps);
    }
    else {
        assertNever(sceneType);
    }
}
function getGreetingSkipToFrame({ fps, scene, messageStartFrame, greetingLoopIndex, sceneTransitionDurationInFrames, }) {
    if (scene.type === "typeOnGreeting") {
        // The first couple frames of the typeOnGreeting scene is the frame are
        // blank. If we skip forward two frames, the user will see the first
        // character of the greeting.
        //
        // Note that even though typeOnGreeting has internal transitions, for
        // some reason we don't need to take into account the fade out
        // transition time. Maybe because the TransitionSeries.Sequence uses the
        // `offset` prop?
        return messageStartFrame + 2;
    }
    else if (scene.type === "greeting") {
        if (greetingLoopIndex === 0) {
            return messageStartFrame;
        }
        else {
            // For all the but the first scene, we need to add add the amount of
            // time the text takes to enter or else we'll skip to before the text
            // is displayed
            return (messageStartFrame +
                toFrames(scene?.betweenMessageTransition?.enterDurationSeconds ?? 0, fps));
        }
    }
    else if (scene.type === "greetingLinesAsScenes" ||
        scene.type === "circleGreeting") {
        if (greetingLoopIndex === 0) {
            return messageStartFrame;
        }
        else {
            // greetingLinesAsScenes and circleGreeting display each greeting as a
            // separate scene. The transitions between scenes overlap and
            // sceneStartFrame starts after the transition of the first
            // greetingLinesAsScenes or circleGreeting scene. So we want to add the
            // duration so that we skip to after the transition is complete
            return messageStartFrame + sceneTransitionDurationInFrames;
        }
    }
    else {
        return messageStartFrame;
    }
}
function getGreetingSceneSubSceneSkipToFrames({ fps, renderConfig, theme, scene, sceneIndex, sceneStartFrame, sceneTransitionDurationInFrames, }) {
    const subSceneStartFrames = [];
    const sceneDurationInFrames = toFrames(getSceneDurationInSeconds(scene, renderConfig, theme), fps);
    if (sceneDurationInFrames === 0) {
        // scene duration of 0 indicates this greeting is not enabled
        return subSceneStartFrames;
    }
    // Loop through the start frames for the messages
    let messageStartFrame = sceneStartFrame;
    const greeting = scene.variant === "intro"
        ? renderConfig.introGreeting
        : renderConfig.outroGreeting;
    const greetingMessages = greeting.messages;
    for (let greetingLoopIndex = 0; greetingLoopIndex < greetingMessages.length; greetingLoopIndex++) {
        const greetingText = greetingMessages[greetingLoopIndex];
        if (greetingText === undefined) {
            throw new Error(`Greeting message at index ${greetingLoopIndex} is undefined`);
        }
        let skipToFrame = getGreetingSkipToFrame({
            fps,
            scene,
            messageStartFrame,
            greetingLoopIndex,
            sceneTransitionDurationInFrames,
        });
        // we want to skip to after the scene transition (not the individual message
        // transition) of the first greeting
        if (greetingLoopIndex === 0) {
            skipToFrame += sceneTransitionDurationInFrames;
        }
        // Note that messageDurationInFrames does not include the transition time
        // between messages
        let messageDurationInFrames = getGreetingMessageDurationInFrames({
            greetingText,
            fps,
            scene,
        });
        if (scene.type === "greetingLinesAsScenes") {
            // we need to add the transition duration to the message duration because
            // app/client/remotionCompositions/Compilation/sceneComponents/GreetingLinesAsScenes.tsx
            // adds it to the duration.
            messageDurationInFrames += sceneTransitionDurationInFrames;
        }
        if (scene.type === "typeOnGreeting" && greetingLoopIndex === 0) {
            // The first greeting in typeOnGreeting is not displayed until after the
            // transition (not just hidden, it's delayed until after the transition).
            // We could add this to its start frame, but to be consistent with how
            // other scenes are handled, we can just consider it as being a bit longer
            // and not showing content during the transition. Also, adding this to the
            // start frame causes other issues and more code to accommodate it.
            messageDurationInFrames += sceneTransitionDurationInFrames;
        }
        subSceneStartFrames.push({
            startFrame: messageStartFrame,
            skipToFrame: skipToFrame,
            sceneType: scene.type,
            sceneIndex,
            itemIndex: greetingLoopIndex,
            variant: scene.variant,
            subSceneDurationInFrames: messageDurationInFrames,
        });
        // update to the start frame of the next message in the loop
        messageStartFrame = getStartFrameOfNextItem({
            currentItemStartFrame: messageStartFrame,
            // Note that messageDurationInFrames does not include the transition time
            // between messages
            currentItemDuration: messageDurationInFrames,
        });
        // greetingLinesAsScenes displays each greeting as a separate scene. The
        // transitions between scenes overlap and sceneStartFrame starts after the
        // transition of the first greetingLinesAsScenes scene. If we just add
        // the message duration to messageStartFrame, we'll end up
        // transitionDurationInSeconds past the start of the next scene. So we need
        // to subtract transitionDurationInSeconds off of messageStartFrame.
        if (scene.type === "greetingLinesAsScenes") {
            messageStartFrame -= sceneTransitionDurationInFrames;
        }
    }
    return subSceneStartFrames;
}
function getCaptionSkipToFrameForClipSeriesBasicClip({ fps, theme, clip, skipToFrame, startFrame, transitionDurationInFrames, }) {
    // We only have a caption if the title is set. If the title is empty and
    // the subtitle is set, we won't have a caption. So we don't need to
    // check the subtitle.
    const hasCaption = Boolean(clip.caption.title);
    // fall back to skipToFrame if there is no caption
    if (!hasCaption) {
        return skipToFrame;
    }
    const startOffsetFrames = toFrames(theme.styles.clips.textVariant.startOffsetSeconds ?? 0, fps);
    const textVariant = theme.styles.clips.textVariant.variant;
    const transitionDurationInFramesToAdd = 
    // These variants calculate startOffsetFrames from the start of the scene,
    // not from after the transition. So we don't want to add the transition
    // duration for them.
    textVariant === "circle" || textVariant === "slide-in-static"
        ? 0
        : transitionDurationInFrames;
    const captionTransitionInFrames = getMaxCaptionTransitionEnterDurationInFrames({
        fps,
        caption: theme.styles.clips.textVariant,
    });
    return (startFrame +
        transitionDurationInFramesToAdd +
        startOffsetFrames +
        captionTransitionInFrames);
}
function getClipSeriesBasicSceneSubSceneSkipToFrames({ fps, renderConfig, theme, scene, sceneIndex, sceneStartFrame, }) {
    const subSceneStartFrames = [];
    // Loop through the start frames for the clips
    let currentClipStartFrame = sceneStartFrame;
    const betweenTransition = theme.styles.clips.betweenTransition;
    const betweenTransitionTiming = getTiming(betweenTransition, fps);
    const betweenTransitionDurationInFrames = betweenTransitionTiming?.getDurationInFrames({ fps }) || 0;
    for (let clipLoopIndex = 0; clipLoopIndex < renderConfig.clips.length; clipLoopIndex++) {
        const isLastClip = clipLoopIndex === renderConfig.clips.length - 1;
        const clip = renderConfig.clips[clipLoopIndex];
        if (!clip) {
            throw new Error(`Clip at index ${clipLoopIndex} is undefined`);
        }
        // We add the transition duration to each clip because
        // TransitionSeries neighboring scenes overlap each other during the
        // transition. Adding the transition duration makes the "previous" scene
        // play until its end and then stop playing (because it's done) while the
        // transition happens. Adding the transition duration here does not make the
        // scene longer since the clips overlap during transitions.
        const transitionDurationInFrames = betweenTransitionDurationInFrames;
        const { clipDurationWithTransitionInFrames, clipDurationInFrames } = getIndividualClipDurationInFrames({
            clip,
            theme,
            fps,
            isLastClip,
            scene,
        });
        // we want to skip to after the scene transition
        const skipToFrame = currentClipStartFrame + transitionDurationInFrames;
        const startFrame = currentClipStartFrame;
        const captionSkipToFrame = getCaptionSkipToFrameForClipSeriesBasicClip({
            fps,
            theme,
            clip,
            skipToFrame,
            startFrame,
            transitionDurationInFrames,
        });
        subSceneStartFrames.push({
            startFrame,
            skipToFrame,
            captionSkipToFrame,
            sceneType: scene.type,
            sceneIndex,
            itemIndex: clipLoopIndex,
            subSceneDurationInFrames: clipDurationWithTransitionInFrames,
        });
        currentClipStartFrame = getStartFrameOfNextItem({
            currentItemStartFrame: currentClipStartFrame,
            currentItemDuration: clipDurationInFrames,
        });
    }
    return subSceneStartFrames;
}
/**
 * The entire purpose of this function is to get the frame that to skip to if
 * you want to view a scene/sub-scene. The result of this function should likely
 * not be used for any other purpose because the way it identifies each
 * scene/sub-scene, it is impossible to get the skipFrame for a specific
 * scene/sub-scene. It's really only useful for skipping to the first instance
 * of a specific kind of scene/sub-scene (not to a specific scene/sub-scene).
 */
export function getSubSceneSkipToFrames(renderConfig, theme) {
    const fps = renderConfig.videoFrame.fps;
    let currentSceneStartFrame = 0;
    let subSceneStartFrames = [];
    theme.scenes.forEach((scene, sceneIndex) => {
        const isFirstScene = sceneIndex === 0;
        const nextSceneStartFrame = getNextSceneStartFrame({
            renderConfig,
            theme,
            fps,
            currentSceneStartFrame,
            currentScene: scene,
        });
        const { transitionDurationInFrames: sceneTransitionDurationInFrames } = getTransitionDuration({
            exitTransition: scene.exitTransition,
            sceneTransition: theme.styles.sceneTransition,
            fps,
        });
        if (getIsGreetingScene(scene)) {
            subSceneStartFrames = subSceneStartFrames.concat(getGreetingSceneSubSceneSkipToFrames({
                fps,
                renderConfig,
                theme,
                scene,
                sceneIndex,
                sceneStartFrame: currentSceneStartFrame,
                sceneTransitionDurationInFrames,
            }));
        }
        else if (scene.type === "clip-series-basic") {
            subSceneStartFrames = subSceneStartFrames.concat(getClipSeriesBasicSceneSubSceneSkipToFrames({
                fps,
                renderConfig,
                theme,
                scene,
                sceneIndex,
                sceneStartFrame: currentSceneStartFrame,
            }));
        }
        else {
            subSceneStartFrames.push({
                startFrame: currentSceneStartFrame,
                skipToFrame: isFirstScene
                    ? currentSceneStartFrame
                    : currentSceneStartFrame + sceneTransitionDurationInFrames,
                sceneType: scene.type,
                sceneIndex,
                itemIndex: 0,
                subSceneDurationInFrames: nextSceneStartFrame -
                    currentSceneStartFrame +
                    sceneTransitionDurationInFrames,
            });
        }
        // set currentSceneStartFrame for the next scene in the loop
        currentSceneStartFrame = nextSceneStartFrame;
    });
    const subSceneStartFramesWithIndex = subSceneStartFrames.map((subSceneStartFrame, index) => ({
        ...subSceneStartFrame,
        subSceneSkipToFrameIndex: index,
        endFrame: subSceneStartFrame.startFrame +
            subSceneStartFrame.subSceneDurationInFrames,
    }));
    assertSubSceneStartFramesDontOverlap(subSceneStartFramesWithIndex);
    return fixSkipToFrameOverlap(subSceneStartFramesWithIndex);
}
/**
 * If skipToFrame goes over the startFrame of the next scene, we'll break the
 * next/previous scene skip functionality. So if that happens, just make
 * skipToFrame equal to the startFrame of the next scene - 1.
 *
 * This can happen for very short scenes where the transition time is longer
 * than the scene itself.
 */
function fixSkipToFrameOverlap(subSceneStartFrames) {
    // start at `length - 2` because the last scene doesn't have a next scene to
    // overlap with
    for (let i = 0; i < subSceneStartFrames.length - 2; i++) {
        const currentSubSceneStartFrame = subSceneStartFrames[i];
        const nextSubSceneStartFrame = subSceneStartFrames[i + 1];
        if (!currentSubSceneStartFrame || !nextSubSceneStartFrame) {
            throw new Error(`SubSceneSkipToFrame is missing at index ${i} or ${i + 1}`);
        }
        if (currentSubSceneStartFrame.skipToFrame >= nextSubSceneStartFrame.startFrame) {
            currentSubSceneStartFrame.skipToFrame =
                nextSubSceneStartFrame.startFrame - 1;
        }
    }
    return subSceneStartFrames;
}
function assertSubSceneStartFramesDontOverlap(subSceneStartFrames) {
    // end at `length - 2` because the last scene doesn't have a next scene to
    // overlap with
    for (let i = 0; i < subSceneStartFrames.length - 2; i++) {
        const currentSubSceneStartFrame = subSceneStartFrames[i];
        const nextSubSceneStartFrame = subSceneStartFrames[i + 1];
        if (!currentSubSceneStartFrame || !nextSubSceneStartFrame) {
            throw new Error(`SubSceneSkipToFrame is missing at index ${i} or ${i + 1}`);
        }
        if (currentSubSceneStartFrame.startFrame >= nextSubSceneStartFrame.startFrame) {
            console.error(`SubSceneSkipToFrames overlap. startFrame of sub-scene ${i} (${currentSubSceneStartFrame.startFrame}) overlaps with startFrame of sub-scene ${i + 1} (${nextSubSceneStartFrame.startFrame})`);
        }
    }
}
/**
 * Returns the first sub-scene matching the provided criteria.
 */
export function getSubSceneSkipToInfo(subSceneSkipToFrames, 
/** All filter conditions must match to get a result. */
filters) {
    return subSceneSkipToFrames.find((subSceneSkipToFrame) => {
        // NOTE: We return false if one condition doesn't match and then finally
        // return true at the end because we want *all* the conditions to match.
        const { itemIndex, variant } = filters;
        if ("sceneTypes" in filters) {
            const sceneTypes = filters.sceneTypes;
            if (!sceneTypes.includes(subSceneSkipToFrame.sceneType)) {
                return false;
            }
        }
        else {
            const sceneIndexes = filters.sceneIndexes;
            if (!sceneIndexes.includes(subSceneSkipToFrame.sceneIndex)) {
                return false;
            }
        }
        if (itemIndex !== undefined &&
            subSceneSkipToFrame.itemIndex !== itemIndex) {
            return false;
        }
        if (variant !== undefined && subSceneSkipToFrame.variant !== variant) {
            return false;
        }
        return true;
    });
}
/**
 * Get the info of the last sub-scene in the subSceneSkipToFrames array.
 */
export function getEndSubSceneSkipToInfo(subSceneSkipToFrames) {
    const subSceneSkipToFrameIndex = subSceneSkipToFrames.length - 1;
    return subSceneSkipToFrames[subSceneSkipToFrameIndex];
}
/**
 * Get the sub-scene that is before the scene with the provided frame in it.
 */
export function getSubSceneSkipToInfoBeforeFrame(subSceneSkipToFrames, frame) {
    const subSceneInfoForFrame = getLastSubSceneSkipToInfoWithFrame(subSceneSkipToFrames, frame);
    if (!subSceneInfoForFrame) {
        return undefined;
    }
    const previousSubSceneSkipToFrameIndex = subSceneInfoForFrame.subSceneSkipToFrameIndex - 1;
    return subSceneSkipToFrames[previousSubSceneSkipToFrameIndex];
}
/**
 * Get the sub-scene that is after the scene with the provided frame in it.
 */
export function getSubSceneSkipToInfoAfterFrame(subSceneSkipToFrames, frame) {
    const subSceneInfoForFrame = getLastSubSceneSkipToInfoWithFrame(subSceneSkipToFrames, frame);
    if (!subSceneInfoForFrame) {
        return undefined;
    }
    const nextSubSceneSkipToFrameIndex = subSceneInfoForFrame.subSceneSkipToFrameIndex + 1;
    return subSceneSkipToFrames[nextSubSceneSkipToFrameIndex];
}
/**
 * Get the last sub-scene that includes the scene with the provided frame in it.
 * The reason you want the last scene is that you know the frame comes after the
 * startFrame of the scene. So even if the frame is before the end of the
 * previous scene (e.g. because scenes overlap during transitions), you know the
 * scene that this function returns will have started after this frame.
 *
 * It's worth noting with determining the sub-scene based on the current frame
 * that, since sub-scenes overlap during transitions, there are certain frames
 * that are technically in two sub-scenes. So you either need to get the first
 * or the last sub-scene that has the frame, depending on your specific need.
 */
export function getLastSubSceneSkipToInfoWithFrame(subSceneSkipToFrames, frame) {
    if (frame < 0) {
        return undefined;
    }
    // need to start at the last sub-scene and work backwards because if we start
    // at the beginning, the frame comparison will always be true for the first
    // scene (`frame` will always be >= 0)
    for (let i = subSceneSkipToFrames.length - 1; i >= 0; i--) {
        const subSceneSkipToFrame = subSceneSkipToFrames[i];
        // should never happen; just here for TS
        if (!subSceneSkipToFrame) {
            return undefined;
        }
        // this should return the last sub-scene if we're at the end of the loop
        if (frame >= subSceneSkipToFrame.startFrame) {
            return subSceneSkipToFrame;
        }
    }
    return undefined;
}
/**
 * Since we can skip to skipToFrame in the sub-scene and this can come after
 * startFrame, we can't use startFrame alone to figure out if we're at the
 * "beginning" of the sub-scene. Instead, we'll consider anywhere between
 * startFrame and skipToFrame as the "beginning" of the sub-scene.
 *
 * Example of why we can't just use startFrame to figure this out: You want to
 * add a button that skips back to the beginning of the scene (or if you're
 * already at the beginning, skips to the previous scene). If you skip back to
 * skipToFrame and then just use startFrame to figure out if you're at the
 * beginning, you'll never think you're at the beginning because you're e.g. a
 * few frames beyond startFrame (at skipToFrame), so you'll keep trying to skip
 * back to the beginning of the scene and end up a few frames after (at
 * skipToFrame) and never reach the beginning and never skip to the previous
 * scene.
 */
export function getIsSubSceneBeginning(subSceneSkipToFrame, frame) {
    return (frame >= subSceneSkipToFrame.startFrame &&
        frame <= subSceneSkipToFrame.skipToFrame);
}
export function getTransitionTimeToNextSceneInFrames(subSceneSkipToFrames, subScene) {
    const nextSubSceneSkipToFrame = subSceneSkipToFrames[subScene.subSceneSkipToFrameIndex + 1];
    if (nextSubSceneSkipToFrame === undefined) {
        return 0;
    }
    // Assuming there is a transition, the endFrame of this scene should come
    // after the startFrame of the next scene.
    const overlap = subScene.endFrame - nextSubSceneSkipToFrame.startFrame;
    return Math.max(0, overlap);
}
